import React, { useMemo, useState, useRef, useEffect } from 'react';

/**
 * RecipientPicker — chip-style multi-select for email recipients with a
 * team-member dropdown + optional external-email free-text input.
 *
 * Props:
 *   selected            string[]    — currently selected emails
 *   onChange            (arr) => void
 *   teamMembers         [{ user_id, name, email }]
 *   canEmailExternal    boolean     — when false, the external input is hidden
 *   extInput            string      — controlled external text input
 *   onExtInputChange    (v) => void
 *   commitExt           (email)     — adds email to selected[] and clears extInput
 *   field               string      — used for data-testid prefixing
 */
const RecipientPicker = ({
  selected, onChange, teamMembers, canEmailExternal,
  extInput, onExtInputChange, commitExt, field = 'recipients',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedSet = useMemo(() => new Set(selected.map(s => s.toLowerCase())), [selected]);
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return teamMembers.filter(m => {
      if (selectedSet.has(m.email.toLowerCase())) return false;
      if (!q) return true;
      return m.email.toLowerCase().includes(q) || (m.name || '').toLowerCase().includes(q);
    });
  }, [teamMembers, search, selectedSet]);

  const remove = (email) => onChange(selected.filter(e => e.toLowerCase() !== email.toLowerCase()));
  const add = (email) => {
    if (!email || selectedSet.has(email.toLowerCase())) return;
    onChange([...selected, email]);
  };

  // Treat any selected email NOT in the team-members list as "external" so
  // we can render it with a small badge.
  const memberEmailSet = useMemo(
    () => new Set(teamMembers.map(m => m.email.toLowerCase())),
    [teamMembers],
  );

  return (
    <div ref={wrapRef} className="relative" data-testid={`${field}-picker`}>
      <div
        className="min-h-[40px] w-full border border-gray-300 rounded-lg px-2 py-1 flex flex-wrap items-center gap-1 focus-within:border-blue-400 bg-white"
        onClick={() => setOpen(true)}
      >
        {selected.map((email) => {
          const member = teamMembers.find(m => m.email.toLowerCase() === email.toLowerCase());
          const isExternal = !memberEmailSet.has(email.toLowerCase());
          return (
            <span
              key={email}
              className={`inline-flex items-center text-xs rounded px-2 py-0.5 ${
                isExternal ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
              }`}
              data-testid={`${field}-chip-${email}`}
            >
              {member ? `${member.name || member.email} <${email}>` : email}
              {isExternal && <span className="ml-1 text-[10px] uppercase tracking-wide">ext</span>}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(email); }}
                className="ml-1.5 text-current opacity-60 hover:opacity-100"
                aria-label={`Remove ${email}`}
                data-testid={`${field}-remove-${email}`}
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? 'Click to pick team members...' : ''}
          className="flex-1 min-w-[120px] text-sm bg-transparent border-0 outline-none px-1 py-0.5"
          data-testid={`${field}-search-input`}
        />
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid={`${field}-dropdown`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">
              {teamMembers.length === 0
                ? 'No team members found.'
                : 'No matches. Search by name or email.'}
            </div>
          ) : (
            filtered.map((m) => (
              <button
                key={m.user_id || m.email}
                type="button"
                onClick={() => { add(m.email); setSearch(''); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex items-center justify-between"
                data-testid={`${field}-option-${m.email}`}
              >
                <span className="font-medium text-gray-800">{m.name || m.email}</span>
                <span className="text-xs text-gray-500 ml-3">{m.email}</span>
              </button>
            ))
          )}
          {canEmailExternal && (
            <div className="border-t border-gray-100 p-2 bg-gray-50">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Add external email</div>
              <div className="flex gap-1">
                <input
                  type="email"
                  value={extInput}
                  onChange={(e) => onExtInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitExt(extInput); }
                  }}
                  placeholder="external@partner.com"
                  className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                  data-testid={`${field}-external-input`}
                />
                <button
                  type="button"
                  onClick={() => commitExt(extInput)}
                  disabled={!extInput?.trim()}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40"
                  data-testid={`${field}-external-add`}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipientPicker;
